import React from 'react';
import { motion } from 'framer-motion';
import { FaFemale, FaCheckCircle } from 'react-icons/fa';
import AnimatedSection, { StaggerContainer, StaggerItem } from '../ui/AnimatedSection';
import womenEmpowermentImg from '../../assets/images/women-empowerment.png';

const WomenEmpowered = () => {
  return (
    <section style={{
      padding: '6rem 0',
      background: 'linear-gradient(180deg, var(--navy-deep) 0%, #0c0f1c 100%)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative Background Elements */}
      <div style={{
        position: 'absolute',
        top: '-10%',
        right: '-10%',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(138, 79, 255, 0.04) 0%, transparent 70%)',
        pointerEvents: 'none'
      }} />

      <div className="container-xxl px-4 px-lg-5">
        <div className="row align-items-center g-5">
          {/* Left Column: Media / Image */}
          <div className="col-lg-6">
            <AnimatedSection variant="fadeLeft">
              <div style={{ position: 'relative' }}>
                {/* Image shadow glowing accent */}
                <div style={{
                  position: 'absolute',
                  inset: '15px -15px -15px 15px',
                  border: '1px solid var(--purple-accent-border)',
                  zIndex: 0,
                  opacity: 0.5
                }} />
                
                <img
                  src={womenEmpowermentImg}
                  alt="Women Maritime Executive Leadership at ISAAC"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover',
                    borderRadius: '2px',
                    position: 'relative',
                    zIndex: 1,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                    border: '1px solid rgba(255,255,255,0.08)'
                  }}
                  loading="lazy"
                />

                {/* Overlaid Badge */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  style={{
                    position: 'absolute',
                    bottom: '25px',
                    left: '25px',
                    background: 'rgba(7, 8, 13, 0.85)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '2px',
                    padding: '1rem 1.5rem',
                    zIndex: 2,
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'var(--purple-accent-dim)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FaFemale color="var(--purple-accent-light)" size={14} />
                  </div>
                  <div>
                    <h5 style={{ color: '#fff', fontSize: '0.8rem', fontWeight: 700, margin: 0, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                      Women Led & Operated
                    </h5>
                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', margin: 0 }}>
                      Leading maritime logistics with integrity
                    </p>
                  </div>
                </motion.div>
              </div>
            </AnimatedSection>
          </div>

          {/* Right Column: Copy & Presentation */}
          <div className="col-lg-6">
            <AnimatedSection variant="fadeRight">
              <span className="section-tag" style={{ 
                color: 'var(--purple-accent-light)',
                background: 'var(--purple-accent-dim)',
                borderColor: 'var(--purple-accent-border)'
              }}>
                Corporate Leadership
              </span>
              
              <h2 style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 800,
                fontSize: 'clamp(2rem, 3.5vw, 2.6rem)',
                color: '#FFFFFF',
                lineHeight: 1.15,
                marginTop: '0.75rem',
                marginBottom: '1.5rem',
                letterSpacing: '-0.02em'
              }}>
                A Proudly <span style={{ color: 'var(--purple-accent-light)' }}>Women-Empowered</span> Shipping & Logistics Entity
              </h2>
              
              <div className="section-divider" style={{ width: '40px', background: 'var(--purple-accent-light)', height: '2px', margin: '1.5rem 0' }} />
              
              <p style={{
                color: 'rgba(255, 255, 255, 0.75)',
                fontSize: '0.98rem',
                lineHeight: 1.8,
                marginBottom: '1.5rem'
              }}>
                ISAAC Logistic Solutions India Pvt. Ltd. stands as a premier women-led entity in India's maritime sector, bringing professional diversity, rigorous compliance, and fresh leadership perspectives to the logistics and shipping industry.
              </p>
              
              <p style={{
                color: 'rgba(255, 255, 255, 0.55)',
                fontSize: '0.9rem',
                lineHeight: 1.7,
                marginBottom: '2rem'
              }}>
                We are actively breaking barriers in a traditionally male-dominated field. Our leadership team sets new benchmarks for operational quality, environmental stewardship, and modern supply chain integrations across key subcontinent shipping terminals.
              </p>

              <StaggerContainer className="row g-3" staggerDelay={0.08}>
                <StaggerItem className="col-sm-6">
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                    <FaCheckCircle color="var(--purple-accent-light)" size={16} style={{ marginTop: '3px', flexShrink: 0 }} />
                    <div>
                      <h4 style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                        Inclusive Culture
                      </h4>
                      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', lineHeight: 1.4, textAlign: 'justify' }}>
                        Fostering diversity in port and operations roles.
                      </p>
                    </div>
                  </div>
                </StaggerItem>

                <StaggerItem className="col-sm-6">
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                    <FaCheckCircle color="var(--purple-accent-light)" size={16} style={{ marginTop: '3px', flexShrink: 0 }} />
                    <div>
                      <h4 style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                        Operational Rigor
                      </h4>
                      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', lineHeight: 1.4, textAlign: 'justify' }}>
                        Delivering extreme safety, turnaround speed, and compliance.
                      </p>
                    </div>
                  </div>
                </StaggerItem>
              </StaggerContainer>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WomenEmpowered;
